import React, { Dispatch, SetStateAction, useMemo } from 'react'
import { createContext } from 'lib/createContext'
import { Loading, Message } from '@workwhile/ui'
import { parseErrorMessage } from 'api'
import { useJurisdictionsQuery } from 'queries/wage/useJurisdictionsQuery'
import { useQueryState, parseAsString } from 'nuqs'
import { useFuzzySearch, FuzzySearchOptions } from '@workwhile/utils'
import { useJurisdictionFiltersQueryState } from '../hooks/useJurisdictionFiltersQueryState'

type Jurisdictions = NonNullable<
  ReturnType<typeof useJurisdictionsQuery>['data']
>
type Jurisdiction = Jurisdictions[number]

type JurisdictionsContextState = {
  state: {
    searchInput: string
    suggestedMatch: string
    filters: Pick<
      ReturnType<typeof useJurisdictionFiltersQueryState>[0],
      'level' | 'stateCode'
    >
    filteredData: Jurisdictions
    dataUpdatedAt: number
    isFetching: boolean
    isError: boolean
    error: string
    pagination: {
      pageIndex: number
      pageSize: number
    }
  }
  actions: {
    refetch: () => void
    setSearchInput: (input: string) => void
    setFilters: Dispatch<
      SetStateAction<ReturnType<typeof useJurisdictionFiltersQueryState>[0]>
    >
    setPagination: Dispatch<
      SetStateAction<JurisdictionsContextState['state']['pagination']>
    >
  }
}

const [Context, useJurisdictionsContextValue] =
  createContext<JurisdictionsContextState>({
    name: 'JurisdictionsContext',
  })

const fuzzySearchOptions: FuzzySearchOptions<Jurisdiction> = {
  threshold: 0.7,
  keySelector: (obj) => [obj.name, obj.id.toString()],
}

export const JurisdictionsProvider = ({
  children,
}: {
  children: React.ReactNode
}) => {
  const [searchInput, setSearchInput] = useQueryState(
    'search',
    parseAsString.withDefault('')
  )

  const [{ level, stateCode, pageIndex, pageSize }, setFilters] =
    useJurisdictionFiltersQueryState()

  const {
    data,
    isLoading,
    isError,
    error,
    dataUpdatedAt,
    refetch,
    isFetching,
  } = useJurisdictionsQuery()

  const preFiltered = useMemo(() => {
    if (!data) return []
    return data.filter((j) => {
      if (level && j.level !== level) return false
      if (stateCode && j.stateCode?.toUpperCase() !== stateCode.toUpperCase())
        return false
      return true
    })
  }, [data, level, stateCode])

  const fuzzySearch = useFuzzySearch(fuzzySearchOptions, preFiltered)

  const { results, bestPossibleMatch } = useMemo(
    () => fuzzySearch(searchInput.trim()),
    [searchInput, fuzzySearch]
  )

  const suggestedMatch =
    bestPossibleMatch &&
    bestPossibleMatch.name.toLowerCase() !== searchInput.trim().toLowerCase()
      ? bestPossibleMatch.name
      : ''

  if (isLoading) {
    return <Loading />
  }

  if (isError) {
    return (
      <Message variant="error">
        Sorry, something went wrong. Please reach out to eng support if this
        persists.
      </Message>
    )
  }

  const value: JurisdictionsContextState = {
    state: {
      searchInput,
      suggestedMatch,
      filters: { level, stateCode },
      filteredData: searchInput.trim() ? results : preFiltered,
      dataUpdatedAt,
      isFetching,
      isError,
      error: parseErrorMessage(error),
      pagination: { pageIndex, pageSize },
    },
    actions: {
      refetch,
      setSearchInput,
      setFilters,
      setPagination: (updater) => {
        const next =
          typeof updater === 'function'
            ? updater({ pageIndex, pageSize })
            : updater
        setFilters((prev) => ({ ...prev, ...next }))
      },
    },
  }

  return <Context.Provider value={value}>{children}</Context.Provider>
}

export { useJurisdictionsContextValue }
